/**
 * TODO - preamble
 */
import { FormattedHTMLMessage } from "react-intl";
import { produce } from "immer";

import { VerificationStepsEnum } from "../../lib/types/runtimeTypes";
import {
  type VerificationService,
  type Organization,
  type EmploymentPersonalInfoViewModel,
  type CustomFormFieldConfig,
  type FormSelectChoice,
  type State,
  type FieldId,
} from "../../lib/types/types";
import {
  handleEmailOnKeyDown,
  updateFieldValidationErrorsByFieldId,
  updateViewModelOrganization,
  shouldCollectAddressFields,
  getFieldConfigByFieldId,
} from "../../lib/utils/stepComponentHelpers/stepComponentHelpers";
import { PhoneNumberComponent as PhoneNumber } from "../FormFields/PhoneNumber/PhoneNumberComponent";
import { FirstNameComponent as FirstName } from "../FormFields/FirstName/FirstNameComponent";
import { LastNameComponent as LastName } from "../FormFields/LastName/LastNameComponent";
import { EmailComponent as Email } from "../FormFields/Email/EmailComponent";
import { MarketConsentWrapperComponent as MarketConsentWrapper } from "../FormFields/MarketConsentWrapper/MarketConsentWrapperComponent";
import { AddressComponent } from "../FormFields/Address/AddressComponent";
import { CityComponent } from "../FormFields/City/City";
import { StateSelectComponent } from "../FormFields/State/StateSelectComponent";
import { CompanyComponent } from "../FormFields/Company/CompanyComponent";
import { PostalCodeComponent } from "../FormFields/PostalCode/PostalCodeComponent";
import { CountryComponentWrapper } from "../FormFields/Country/CountryComponentWrapper";
import { getOptions } from "../../options/options";
import { FormFieldCustom } from "../FormFields/FormFieldCustom/FormFieldCustom";
import { ExtraMessage } from "../ExtraMessage";
import { ChangeLocaleV2Component } from "../FormFields/ChangeLocale/ChangeLocaleComponent";
import { PersonalInfoWrapper } from "../PersonalInfoStepWrapper/PersonalInfoStepWrapper";
import { PersonalInfoStepSubmitButton } from "../PersonalInfoStepSubmitButton/PersonalInfoStepSubmitButton";
import { PersonalInfoHeader } from "../FormHeader/StepHeaders/PersonalInfoHeaderComponent";
import { CaptchaComponent } from "../FormFields/Captcha/CaptchaComponent";

interface StepEmploymentPersonalInfoComponentProps {
  verificationService: VerificationService;
}

export const StepEmploymentPersonalInfoComponent = ({
  verificationService,
}: StepEmploymentPersonalInfoComponentProps) => {
  const viewModel = verificationService.viewModel as EmploymentPersonalInfoViewModel;
  const { fieldValidationErrors } = verificationService;
  const options = getOptions();

  const updateEmploymentViewModel = (key: keyof EmploymentPersonalInfoViewModel, value: any) => {
    const nextState: EmploymentPersonalInfoViewModel = produce(
      viewModel,
      (draft: EmploymentPersonalInfoViewModel) => {
        (draft[key] as any) = value;
      },
    );
    verificationService.updateViewModel(nextState);
  };

  return (
    <PersonalInfoWrapper
      verificationService={verificationService}
      step={verificationService.verificationResponse.currentStep}
    >
      <PersonalInfoHeader
        verificationService={verificationService}
        classNameIdentifier="employment"
      />

      <ChangeLocaleV2Component verificationService={verificationService} />
      <CountryComponentWrapper
        verificationService={verificationService}
        disabled={getFieldConfigByFieldId("country")?.disabled}
        viewModelDraftDecorator={(draft, countryChoice) => {
          const viewModel = draft as EmploymentPersonalInfoViewModel;

          viewModel.shouldCollectAddress = shouldCollectAddressFields(
            countryChoice,
            verificationService.programTheme.config.countries,
          );
          const addressFields: FieldId[] = ["address1", "city", "state", "postalCode"];
          const existing = (viewModel.fieldsToSkipValidation || []).filter(
            (id) => !addressFields.includes(id),
          );
          if (!viewModel.shouldCollectAddress) {
            viewModel.address1 = "";
            viewModel.city = "";
            viewModel.state = "";
            viewModel.postalCode = "";
            viewModel.fieldsToSkipValidation = [...existing, ...addressFields];
          } else {
            viewModel.fieldsToSkipValidation = existing.length ? existing : undefined;
          }
        }}
      />

      <CompanyComponent
        value={(verificationService.viewModel as EmploymentPersonalInfoViewModel).organization}
        verificationService={verificationService}
        isErrored={!!fieldValidationErrors.organization}
        disabled={getFieldConfigByFieldId("organization")?.disabled}
        onChange={(choice: Organization) => {
          updateViewModelOrganization(choice, verificationService);
          updateFieldValidationErrorsByFieldId("organization", choice, verificationService);
        }}
      />

      <div className="sid-names">
        <FirstName
          value={viewModel.firstName}
          isErrored={!!fieldValidationErrors.firstName}
          disabled={getFieldConfigByFieldId("firstName")?.disabled}
          onChange={(newValue) => {
            updateEmploymentViewModel("firstName", newValue);
            updateFieldValidationErrorsByFieldId("firstName", newValue, verificationService);
          }}
        />

        <LastName
          value={viewModel.lastName}
          isErrored={!!fieldValidationErrors.lastName}
          disabled={getFieldConfigByFieldId("lastName")?.disabled}
          onChange={(newValue) => {
            updateEmploymentViewModel("lastName", newValue);
            updateFieldValidationErrorsByFieldId("lastName", newValue, verificationService);
          }}
        />
      </div>

      {shouldCollectAddressFields(
        viewModel.countryChoice,
        verificationService.programTheme.config.countries,
      ) ? (
        <AddressComponent
          isErrored={!!fieldValidationErrors.address1}
          errorId={fieldValidationErrors.address1}
          onChange={(newValue) => {
            updateEmploymentViewModel("address1", newValue);
            updateFieldValidationErrorsByFieldId("address1", newValue, verificationService);
          }}
          isRequired
          value={viewModel.address1}
          disabled={getFieldConfigByFieldId("address1")?.disabled}
          explanation={
            <FormattedHTMLMessage id="addressExplanation" defaultMessage="Use your home address" />
          }
        />
      ) : null}

      {shouldCollectAddressFields(
        viewModel.countryChoice,
        verificationService.programTheme.config.countries,
      ) ? (
        <div className="sid-thirds">
          <CityComponent
            isErrored={!!fieldValidationErrors.city}
            errorId={fieldValidationErrors.city}
            onChange={(newValue) => {
              updateEmploymentViewModel("city", newValue);
              updateFieldValidationErrorsByFieldId("city", newValue, verificationService);
            }}
            value={viewModel.city}
            isRequired
            disabled={getFieldConfigByFieldId("city")?.disabled}
          />
          <StateSelectComponent
            isRequired
            value={viewModel.state || undefined}
            isErrored={Boolean(fieldValidationErrors.state)}
            onChange={async (stateChoice: FormSelectChoice<State, string>) => {
              const stateValue = stateChoice ? stateChoice.value : undefined;
              updateEmploymentViewModel("state", stateValue);
              updateFieldValidationErrorsByFieldId("state", stateValue, verificationService);
            }}
            label={<FormattedHTMLMessage id="state" defaultMessage="State" />}
            disabled={getFieldConfigByFieldId("state")?.disabled}
          />
          <PostalCodeComponent
            isErrored={!!fieldValidationErrors.postalCode}
            onChange={(newValue) => {
              updateEmploymentViewModel("postalCode", newValue);
              updateFieldValidationErrorsByFieldId("postalCode", newValue, verificationService);
            }}
            value={viewModel.postalCode}
            disabled={getFieldConfigByFieldId("postalCode")?.disabled}
          />
        </div>
      ) : null}

      <Email
        value={viewModel.email}
        isErrored={!!fieldValidationErrors.email}
        explanation={
          <FormattedHTMLMessage
            id="emailExplanation"
            defaultMessage="Use your work email if you have one"
          />
        }
        onChange={(newValue) => {
          updateEmploymentViewModel("email", newValue);
          updateFieldValidationErrorsByFieldId("email", newValue, verificationService);
        }}
        onKeyDown={(event) => handleEmailOnKeyDown(event)}
        disabled={getFieldConfigByFieldId("email")?.disabled}
      />
      {verificationService.programTheme.isSmsNotifierConfigured ||
      verificationService.programTheme.smsLoopEnabled ? (
        <PhoneNumber
          isRequired={!!verificationService.programTheme.smsLoopEnabled}
          value={viewModel.phoneNumber}
          isErrored={!!fieldValidationErrors.phoneNumber}
          onChange={(newValue) => {
            updateEmploymentViewModel("phoneNumber", newValue);
            updateFieldValidationErrorsByFieldId("phoneNumber", newValue, verificationService);
          }}
          selectedCountryCode={viewModel.countryChoice && viewModel.countryChoice.value}
          disabled={getFieldConfigByFieldId("phoneNumber")?.disabled}
        />
      ) : null}

      {options.customFormFields.map((config: CustomFormFieldConfig) => (
        <FormFieldCustom config={config} verificationService={verificationService} />
      ))}

      <MarketConsentWrapper
        verificationService={verificationService}
        isErrored={!!fieldValidationErrors.marketConsentValue}
        onChange={(newValue) => {
          updateEmploymentViewModel("metadata", {
            ...verificationService.viewModel.metadata,
            marketConsentValue: newValue,
          });
          updateFieldValidationErrorsByFieldId("marketConsentValue", newValue, verificationService);
        }}
        viewModel={viewModel}
        disabled={getFieldConfigByFieldId("marketConsentValue")?.disabled}
      />

      <ExtraMessage verificationService={verificationService} suffix="CollectAboveSubmit" />

      <CaptchaComponent
        verificationService={verificationService}
        isErrored={!!fieldValidationErrors.captchaToken}
      />

      <PersonalInfoStepSubmitButton
        step={VerificationStepsEnum.collectEmployeePersonalInfo}
        verificationService={verificationService}
        viewModel={viewModel}
        buttonText={
          <FormattedHTMLMessage
            id="step.personalInfo.submitButtonText"
            defaultMessage="Verify My Employment Status"
          />
        }
      />
    </PersonalInfoWrapper>
  );
};
